import { ExportIcon } from 'components/icons';
import { Skeleton } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import { CustomerStatistics, TimeSeries } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { filters } from './categoryFilters';
import { formatNumber } from './helpers';
import { useExecutiveSummaryState } from './useExecutiveSummaryState';

const ExportMenuLazy = lazy(
  () =>
    import('components/analytics/ExecutiveSummary/ExecutiveSummaryExportMenu')
);

interface RowProps {
  field: keyof CustomerStatistics;
  label: string;
  format: 'number' | 'currency' | 'percent';
  selected: number | null;
  previous: number | null;
  change: number | null;
  description: string;
}

type RowKeys = keyof RowProps;

export const ComparisonTable = () => {
  const {
    selectedCategory,
    startDate,
    endDate,
    previousEndDate,
    previousStartDate,
    metricList,
    selectedCategoryMetrics,
  } = useExecutiveSummaryState();

  const { currentStore } = useAppSelector(userDetailsState);
  const { output } = useAppSelector(composableFiltersState);

  const selectedCategoryFilters = useMemo(() => {
    if (selectedCategory === 'overall') {
      return output.filtersApiPayload;
    }

    return filters[selectedCategory]({ startDate, endDate });
  }, [endDate, output.filtersApiPayload, selectedCategory, startDate]);

  const previousCategoryFilters = useMemo(() => {
    if (selectedCategory === 'overall') {
      return output.filtersApiPayload;
    }

    return filters[selectedCategory]({
      startDate: previousStartDate,
      endDate: previousEndDate,
    });
  }, [
    previousEndDate,
    previousStartDate,
    output.filtersApiPayload,
    selectedCategory,
  ]);

  // Static metrics
  const { data: selectedMetrics, isFetching: isSelectedMetricsLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: selectedCategoryFilters,
        params: {
          metric_list: metricList,
          end_date: endDate,
          start_date: startDate,
          store_name: String(currentStore.completeInfo?.store_name),
        },
      },
      { skip: !!!currentStore.completeInfo?.store_name }
    );
  // Static metrics
  const { data: previousMetrics, isFetching: isPreviousMetricsLoading } =
    useGetCustomerStatisticsQuery(
      {
        body: previousCategoryFilters,
        params: {
          metric_list: metricList,
          end_date: previousEndDate,
          start_date: previousStartDate,
          store_name: String(currentStore.completeInfo?.store_name),
        },
      },
      { skip: !!!currentStore.completeInfo?.store_name }
    );

  const columns = useMemo(
    () => [
      { accessor: 'label', label: '' },
      {
        accessor: 'selected',
        label: 'Selected',
        extra: `${startDate} - ${endDate}`,
      },
      {
        accessor: 'previous',
        label: 'Previous',
        extra: `${previousStartDate} - ${previousEndDate}`,
      },
      { accessor: 'change', label: 'Change' },
    ],
    [startDate, endDate, previousStartDate, previousEndDate]
  );

  const rows = useMemo(() => {
    const out: RowProps[] = selectedCategoryMetrics.map((row) => ({
      field: row.id,
      label: row.label,
      format: row.format,
      selected: null,
      previous: null,
      description: row.description,
      get change() {
        if (this.previous && this.selected) {
          return (this.selected - this.previous) / this.previous;
        }
        return null;
      },
    }));

    if (selectedMetrics) {
      out.forEach((row) => {
        const metricId = row.field as keyof CustomerStatistics;
        const metric = selectedMetrics[metricId] as TimeSeries[];

        row.selected = metric?.length > 0 ? metric[0].value : null;
      });
    }
    if (previousMetrics) {
      out.forEach((row) => {
        const metricId = row.field as keyof CustomerStatistics;
        const metric = previousMetrics[metricId] as TimeSeries[];

        row.previous = metric?.length > 0 ? metric[0].value : null;
      });
    }

    return out;
  }, [selectedCategoryMetrics, selectedMetrics, previousMetrics]);

  return (
    <BaseTable
      columns={columns}
      rows={rows}
      isPreviousMetricsLoading={isPreviousMetricsLoading}
      isSelectedMetricsLoading={isSelectedMetricsLoading}
    />
  );
};

const BaseTable = ({
  columns,
  rows,
  isPreviousMetricsLoading,
  isSelectedMetricsLoading,
}: {
  columns: {
    accessor: string;
    label: string;
    extra?: string;
  }[];
  rows: RowProps[];
  isPreviousMetricsLoading: boolean;
  isSelectedMetricsLoading: boolean;
}) => {
  const { currentStore } = useAppSelector(userDetailsState);
  const currency = currentStore?.completeInfo?.currency || 'USD';
  const locale = currentStore?.completeInfo?.primary_locale || 'en-US';

  const {
    selectedCategory,
    startDate,
    endDate,
    previousEndDate,
    previousStartDate,
  } = useExecutiveSummaryState();

  const selectedDateRange = useMemo(() => {
    return `${format(new Date(startDate), 'yyyy-MM-dd')} - ${format(
      new Date(endDate),
      'yyyy-MM-dd'
    )}`;
  }, [startDate, endDate]);

  const previousDateRange = useMemo(() => {
    return `${format(new Date(previousStartDate), 'yyyy-MM-dd')} - ${format(
      new Date(previousEndDate),
      'yyyy-MM-dd'
    )}`;
  }, [previousStartDate, previousEndDate]);

  const generateCSVData = useCallback(() => {
    const csvHeaders = [
      { label: 'Category', key: 'category' },
      { label: 'Metric', key: 'metric' },
      { label: `Selected (${selectedDateRange})`, key: 'selected' },
      { label: `Previous (${previousDateRange})`, key: 'previous' },
      { label: 'Change', key: 'change' },
      { label: 'Description', key: 'description' },
    ];

    const csvData = rows.map((row) => {
      const { label, selected, previous, change, description, format } = row;
      return {
        category:
          selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1),
        metric: label,
        selected:
          formatNumber(selected, { type: format, locale, currency }) || '',
        previous:
          formatNumber(previous, { type: format, locale, currency }) || '',
        change:
          formatNumber(change, { type: 'percent', locale, currency }) || '',
        description,
      };
    });

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [
    currency,
    locale,
    previousDateRange,
    rows,
    selectedCategory,
    selectedDateRange,
  ]);

  const tableRef = useRef<HTMLTableElement>(null);

  const renderTrendIcon = (value: number) => {
    if (value === 0 || null) return null;

    if (value > 0) {
      return <TrendingUpIcon className="w-5 h-5 text-[#54B363]" />;
    }

    return <TrendingUpIcon className="w-5 h-5 rotate-180 text-[#F87349] " />;
  };

  const storeName = currentStore?.completeInfo?.store_name?.replaceAll(
    ' ',
    '_'
  );
  const filename = `${storeName}-executive_summary_table-${format(
    Date.now(),
    'yyyyMMdd'
  )}`;

  return (
    <div className="mt-[46px]">
      <div className="flex justify-between">
        <p className="text-2xl">All metrics</p>
        <div>
          <Skeleton
            isLoaded={!isPreviousMetricsLoading && !isSelectedMetricsLoading}
          >
            <Suspense
              fallback={
                <div className="flex items-center p-3 rounded-lg bg-segments-gray focus:outline-none">
                  Export
                  <ExportIcon className="w-5 h-5" />
                </div>
              }
            >
              <ExportMenuLazy
                containerRef={tableRef}
                filename={filename}
                generateCSVData={generateCSVData}
              >
                <div className="p-3 rounded-full bg-segments-gray focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                  <ExportIcon className="w-5 h-5" />
                </div>
              </ExportMenuLazy>
            </Suspense>
          </Skeleton>
        </div>
      </div>
      <table className="w-full table-auto lg:w-[70%] mt-10" ref={tableRef}>
        <thead>
          <tr className="">
            {columns.map((column) => {
              return (
                <th
                  key={column.accessor}
                  className="w-1/4 pb-4 text-right align-top last:pr-2"
                >
                  <p>{column.label}</p>
                  <span className="font-normal text-black text-opacity-30 text-segments-13px">
                    {column?.extra}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            return (
              <tr
                key={row.field}
                className="border-b even:bg-segments-gray-F8 hover:bg-segments-accent-green-3 group hover:relative"
              >
                {columns.map((column) => {
                  if (column.accessor === 'change') {
                    const value =
                      (row[column.accessor as RowKeys] as number) || 0;

                    const formatedValue = row[
                      column.accessor as RowKeys
                    ]?.toLocaleString(locale, {
                      style: 'percent',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    });

                    return (
                      <td
                        key={column.accessor}
                        className="py-2.5 text-right px-2"
                      >
                        <Skeleton
                          isLoaded={
                            !isPreviousMetricsLoading &&
                            !isSelectedMetricsLoading
                          }
                          className="inline-block w-20"
                        >
                          <div className="flex items-center justify-end space-x-2">
                            <span>{renderTrendIcon(value)}</span>
                            <span>{formatedValue || '--'}</span>
                          </div>
                        </Skeleton>
                        <div className="absolute top-0 left-full ml-8 bg-segments-accent-color-orange bg-opacity-10 w-[280px] rounded-lg border-l-segments-accent-color-orange border-l-[5px] hidden group-hover:block py-4 px-6 text-left text-segments-13px space-y-2">
                          <p className="font-bold capitalize">{row.label}</p>
                          <p>{row.description}</p>
                        </div>
                      </td>
                    );
                  }

                  const value = row[column.accessor as RowKeys]?.toLocaleString(
                    locale,
                    {
                      style: row.format === 'number' ? undefined : row.format,
                      currency: currency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }
                  );

                  const isLoading =
                    (column.accessor === 'previous' &&
                      isPreviousMetricsLoading) ||
                    (column.accessor === 'selected' &&
                      isSelectedMetricsLoading);
                  return (
                    <td
                      key={column.accessor}
                      className={`py-2.5  first:text-left text-right first:pl-2 ${
                        column.accessor === 'selected' &&
                        'text-segments-accent-color-1 font-bold'
                      }`}
                    >
                      <Skeleton
                        isLoaded={!isLoading}
                        className="inline-block w-20"
                      >
                        {value || '--'}
                      </Skeleton>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function TrendingUpIcon({ className }: { className: string }) {
  return (
    <svg
      className={`fill-current ${className}`}
      width="20"
      height="20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path
        d="M5.982 9.666 10 5.648l4.018 4.018M10 6.205v8.147"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
